// The open trip: beside the list on a wide screen, its own page on a phone. Route map, both ends,
// distance and time, and the three things the website can change: category, purpose and notes.

import { useEffect, useMemo, useState } from "react";
import { tintStyle } from "../../components/Category.tsx";
import { Icon } from "../../components/Icon.tsx";
import { useToast } from "../../components/Toast.tsx";
import { type AccountData, useAccount } from "../../data/AccountData.tsx";
import { displayUnit, tripUnit, vehicleById, vehicleOf } from "../../data/derived.ts";
import type { Category } from "../../domain/categories.ts";
import { formatDayTitle, formatDistance, formatDuration, formatSpeed, formatTime } from "../../domain/format.ts";
import { type Trip, averageSpeed, tripDuration, tripTitle } from "../../domain/model.ts";
import { purposeSuggestions } from "../../domain/purpose.ts";
import { RouteMap } from "./RouteMap.tsx";
import { useTripEditor } from "./useTripEditor.ts";

export function TripDetail({ trip, data, onClose, onNewer, onOlder, reviewing, onPicked }: {
  trip: Trip;
  data: AccountData;
  onClose: () => void;
  onNewer: (() => void) | undefined;
  onOlder: (() => void) | undefined;
  /** Review mode: number keys pick a category, and a pick moves on. */
  reviewing: boolean;
  onPicked?: (category: Category) => void;
}) {
  const { loadRoute, reload } = useAccount();
  const toast = useToast();
  const editor = useTripEditor(trip, {
    onSubscriptionEnded: reload,
    onFailed: (retry) => toast({ tone: "warning", text: `Couldn’t save the change to ${tripTitle(trip)}.`, action: { label: "Retry", run: retry } }),
  });
  const [route, setRoute] = useState<[number, number, number][] | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadRoute(trip.id).then((points) => { if (!cancelled) setRoute(points); }).catch(() => { if (!cancelled) setRoute([]); });
    return () => { cancelled = true; };
  }, [loadRoute, trip.id]);

  const vehicles = useMemo(() => vehicleById(data), [data]);
  const vehicle = vehicleOf(trip, vehicles);
  const unit = tripUnit(trip, vehicles, displayUnit(data));
  const category = data.catalog.resolve(editor.draft.categoryRaw);
  const choices = data.catalog.choices(category);
  const duration = tripDuration(trip);
  const speed = averageSpeed(trip);
  const startName = trip.startName ?? "Start";
  const endName = trip.endName ?? "End";
  const history = useMemo(() => data.trips.map((candidate) => ({ purpose: candidate.purpose, date: candidate.start })), [data.trips]);
  const suggestions = purposeSuggestions(history, editor.draft.purpose);

  function pick(choice: Category) {
    editor.setCategory(choice.rawValue);
    onPicked?.(choice);
  }

  // Number keys pick a category while reviewing.
  useEffect(() => {
    if (!reviewing) return;
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if ((event.target as HTMLElement).closest("input, textarea, select")) return;
      const index = Number(event.key) - 1;
      const choice = Number.isInteger(index) ? choices[index] : undefined;
      if (choice) {
        event.preventDefault();
        pick(choice);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <aside className="cv-card cv-detail" aria-labelledby="detail-title">
      <div className="cv-detail-head">
        <h2 id="detail-title">{formatDayTitle(trip.start)}</h2>
        <SaveStatusText status={editor.status} retry={editor.retry} />
        <button className="cv-icon-btn" type="button" aria-label="Newer trip" onClick={onNewer} disabled={!onNewer}><Icon name="chevronUp" /></button>
        <button className="cv-icon-btn" type="button" aria-label="Older trip" onClick={onOlder} disabled={!onOlder}><Icon name="chevronDown" /></button>
        <button className="cv-icon-btn" type="button" aria-label="Close trip" onClick={onClose}><Icon name="close" /></button>
      </div>

      {editor.status.kind === "failed" && (
        <div style={{ margin: "0 16px 12px" }}>
          <div className="cv-notice is-danger" role="alert">
            <Icon name="warning" />
            <p style={{ flex: 1 }}>{editor.status.message}</p>
            <button className="cv-btn cv-btn-secondary" type="button" onClick={editor.retry} style={{ minHeight: 32 }}>Retry</button>
          </div>
        </div>
      )}

      {route === null
        ? <div className="cv-map" aria-hidden="true"><span className="cv-sk" style={{ height: "100%", borderRadius: 0 }} /></div>
        : <RouteMap route={route.map(([lat, lon]) => [lat, lon])} start={trip.startCoordinate} end={trip.endCoordinate} startName={startName} endName={endName} />}

      <div className="cv-ends">
        <span className="cv-end-dot" aria-hidden="true" />
        <div><div className="cv-end-name"><span className="cv-visually-hidden">From </span>{startName}</div></div>
        <div className="cv-end-time">{formatTime(trip.start)}</div>
        <span className="cv-end-rail" aria-hidden="true" /><span /><span />
        <span className="cv-end-dot is-to" aria-hidden="true" />
        <div><div className="cv-end-name"><span className="cv-visually-hidden">To </span>{endName}{trip.isRoundTrip ? " and back" : ""}</div></div>
        <div className="cv-end-time">{trip.end ? formatTime(trip.end) : ""}</div>
      </div>

      <div className="cv-tiles">
        <Tile icon="road" title="Distance" value={formatDistance(trip.meters, unit)} />
        <Tile icon="clock" title="Duration" value={duration !== undefined ? formatDuration(duration) : "—"} />
        <Tile icon="gauge" title="Average speed" value={speed !== undefined ? formatSpeed(speed, unit) : "—"} />
        <Tile icon="car" title="Vehicle" value={vehicle?.name ?? "—"} />
      </div>

      <div className="cv-section">
        <div className="cv-label" id="category-label">
          Category
          {category && trip.classificationSource && editor.draft.categoryRaw === trip.categoryRaw && (
            <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 600 }}>
              <Icon name="wand" />Classified automatically
            </span>
          )}
        </div>
        <div className="cv-cats" role="group" aria-labelledby="category-label">
          {choices.map((choice, index) => (
            <button
              key={choice.rawValue}
              className="cv-cat"
              type="button"
              aria-pressed={choice.rawValue === category?.rawValue}
              style={tintStyle(choice)}
              onClick={() => pick(choice)}
              aria-keyshortcuts={reviewing && index < 9 ? String(index + 1) : undefined}
            >
              <span className="ico"><Icon name={choice.symbol} /></span>{choice.name}
              {reviewing && index < 9 && <span className="cv-kbd" aria-hidden="true">{index + 1}</span>}
            </button>
          ))}
        </div>
      </div>

      {category?.isDeductible && (
        <div className="cv-section">
          <label className="cv-label" htmlFor="trip-purpose">Purpose</label>
          <input
            id="trip-purpose"
            className="cv-field"
            type="text"
            value={editor.draft.purpose}
            placeholder="What the trip was for"
            onChange={(event) => editor.setPurpose(event.target.value)}
            onBlur={editor.flush}
            maxLength={1000}
          />
          {suggestions.length > 0 && (
            <div className="cv-suggest" role="group" aria-label="Recent purposes">
              {suggestions.map((suggestion) => (
                <button key={suggestion} type="button" onClick={() => { editor.setPurpose(suggestion); editor.flush(); }}>{suggestion}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="cv-section">
        <label className="cv-label" htmlFor="trip-notes">Notes</label>
        <textarea
          id="trip-notes"
          className="cv-field"
          value={editor.draft.notes}
          onChange={(event) => editor.setNotes(event.target.value)}
          onBlur={editor.flush}
          maxLength={10000}
        />
      </div>

      <div className="cv-detail-foot">
        <Icon name="phone" />
        <span>Changes save as you go and reach Carveny on your iPhone the next time it opens. Route, times and distance are edited in the app.</span>
      </div>
    </aside>
  );
}

function Tile({ icon, title, value }: { icon: string; title: string; value: string }) {
  return (
    <div className="cv-tile">
      <div className="t"><Icon name={icon} />{title}</div>
      <div className="v">{value}</div>
    </div>
  );
}

function SaveStatusText({ status, retry }: { status: ReturnType<typeof useTripEditor>["status"]; retry: () => void }) {
  return (
    <span className="cv-status" role="status" aria-live="polite">
      {status.kind === "saving" && "Saving…"}
      {status.kind === "saved" && <><Icon name="check" />Saved</>}
      {status.kind === "failed" && <>
        <span style={{ color: "var(--danger)", fontWeight: 600 }}>Not saved</span>
        <button className="cv-btn-plain cv-btn" type="button" onClick={retry} style={{ minHeight: 28, padding: "0 6px" }}>Retry</button>
      </>}
    </span>
  );
}
